import { ZONES_MERCHICH } from './constants.js';

// Génération Ref Global
export const generateRefGlobal = (existingProjects = []) => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = String(now.getFullYear()).slice(-2);
  const count = existingProjects.filter(p => {
    if (!p.date) return false;
    const projectDate = new Date(p.date);
    return projectDate.getMonth() === now.getMonth() && projectDate.getFullYear() === now.getFullYear();
  }).length + 1;
  return `${String(count).padStart(2, '0')}-${month}${year}`;
};

// Génération Numéro Dossier
export const generateNumeroDossier = (existingProjects = []) => {
  const year = new Date().getFullYear();
  const count = existingProjects.filter(p =>
    p.numeroDossier && p.numeroDossier.includes(year.toString())
  ).length + 1;
  return `${String(count).padStart(2, '0')}-${year}`;
};

// Calcul reste à payer
export const calculateResteAPayer = (montantTotal, avances = [], fraisSupplementaires = 0) => {
  const totalAvances = avances.reduce((sum, avance) => sum + (parseFloat(avance.montant) || 0), 0);
  return Math.max(0, (parseFloat(montantTotal) || 0) + (parseFloat(fraisSupplementaires) || 0) - totalAvances);
};

// Calcul total avances
export const calculateTotalAvances = (avances = []) => {
  return avances.reduce((sum, avance) => sum + (parseFloat(avance.montant) || 0), 0);
};

// Format nombre
export const formatNumber = (number) => {
  if (number === null || number === undefined) return '0';
  return new Intl.NumberFormat('fr-FR').format(number);
};

// Format montant
export const formatMontant = (montant) => {
  return `${formatNumber(montant)} DH`;
};

// Format date
export const formatDate = (date) => {
  if (!date) return '-';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '-';
  return dateObj.toLocaleDateString('fr-FR');
};

// Format date heure
export const formatDateTime = (date) => {
  if (!date) return '-';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '-';
  return dateObj.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Debounce
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Validation coordonnées
export const validateCoordinates = (x, y, zone) => {
  const zoneInfo = ZONES_MERCHICH.find(z => z.id === zone);
  if (!zoneInfo || !zoneInfo.bounds) return { valid: false, message: 'Zone invalide' };

  const numX = parseFloat(x);
  const numY = parseFloat(y);

  if (isNaN(numX) || isNaN(numY)) {
    return { valid: false, message: 'Coordonnées non numériques' };
  }

  const { bounds } = zoneInfo;
  const isValid = numX >= bounds.xMin && numX <= bounds.xMax &&
    numY >= bounds.yMin && numY <= bounds.yMax;

  if (!isValid) {
    return {
      valid: false,
      message: `Coordonnées hors limites. X: ${bounds.xMin}-${bounds.xMax}, Y: ${bounds.yMin}-${bounds.yMax}`
    };
  }

  return { valid: true, message: '' };
};

// Détection zone auto
export const detectZoneByLocation = (latitude, longitude) => {
  if (latitude >= 31.5) return "EPSG:26191";
  if (latitude >= 28.0) return "EPSG:26192";
  if (latitude >= 24.0) return "EPSG:26194";
  return "EPSG:26195";
};

// Parse coordonnées texte
export const parseCoordinatesFromText = (text) => {
  if (!text) return [];

  try {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map((line, index) => {
      const parts = line.split(',').map(part => part.trim());
      if (parts.length >= 3) {
        const x = parseFloat(parts[1]);
        const y = parseFloat(parts[2]);
        if (!isNaN(x) && !isNaN(y)) {
          return {
            id: `coord_${Date.now()}_${index}`,
            nom: parts[0] || `Point ${index + 1}`,
            x: x,
            y: y
          };
        }
      }
      return null;
    }).filter(coord => coord !== null);
  } catch (error) {
    console.error('Erreur parse coords:', error);
    return [];
  }
};

// ID unique
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Conversion timestamp Firestore
export const convertFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate();
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  return new Date(timestamp);
};

// Sanitize données projet
export const sanitizeProjectData = (projectData) => {
  const sanitized = { ...projectData };

  // Nettoyer les nombres
  sanitized.montantTotal = parseFloat(projectData.montantTotal) || 0;
  sanitized.fraisSupplementaires = parseFloat(projectData.fraisSupplementaires) || 0;

  // Nettoyer les avances
  sanitized.avances = (projectData.avances || []).map(avance => ({
    ...avance,
    montant: parseFloat(avance.montant) || 0
  }));

  // Nettoyer les coordonnées
  sanitized.coordonnees = (projectData.coordonnees || []).map(coord => ({
    ...coord,
    x: parseFloat(coord.x) || 0,
    y: parseFloat(coord.y) || 0
  }));

  // Supprimer les champs undefined
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// Validation email
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validation téléphone (pas de format obligatoire)
export const validatePhone = (phone) => {
  return true; // Pas de validation de format
};

// Validation CIN (pas de format obligatoire)
export const validateCIN = (cin) => {
  return true; // Pas de validation de format
};

// Obtenir nom court de zone
export const getZoneShortName = (zoneId) => {
  const zone = ZONES_MERCHICH.find(z => z.id === zoneId);
  return zone ? zone.shortName : zoneId;
};

// Formater le régime foncier avec référence
export const formatRegimeDisplay = (regimeFoncier, regimeReference) => {
  if (!regimeFoncier) return '-';

  if (regimeFoncier === 'Titre' && regimeReference) {
    return `T${regimeReference}`;
  }
  if (regimeFoncier === 'Réquisition' && regimeReference) {
    return `R${regimeReference}`;
  }

  return regimeFoncier;
};

// Obtenir nom complet de zone
export const getZoneName = (zoneId) => {
  const zone = ZONES_MERCHICH.find(z => z.id === zoneId);
  return zone ? zone.name : zoneId;
};

// Clients d'un projet : nouveau format (tableau) ou champs historiques client/cin
export const normalizeClients = (project) => {
  if (Array.isArray(project?.clients) && project.clients.length > 0) {
    return project.clients.map((c, i) => ({
      id: c.id || `client-${i}`,
      type: c.type === 'societe' ? 'societe' : 'personne',
      nom: c.nom || '',
      cin: c.cin || '',
      ice: c.ice || '',
      telephone: c.telephone || ''
    }));
  }
  return [{
    id: 'client-0',
    type: 'personne',
    nom: project?.client || '',
    cin: project?.cin || '',
    ice: '',
    telephone: ''
  }];
};

// Libellé affiché pour la liste / le tri / le reçu
export const buildClientLabel = (clients = []) =>
  clients.map(c => (c.nom || '').trim()).filter(Boolean).join(' / ');

// Identifiant d'un client (CIN ou ICE)
export const clientIdentifier = (client) =>
  client?.type === 'societe'
    ? (client.ice ? `ICE ${client.ice}` : '')
    : (client?.cin ? `CIN ${client.cin}` : '');

// Recherche projets
export const searchProjects = (projects, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') return projects;

  const term = searchTerm.toLowerCase().trim();
  return projects.filter(project => {
    return (
      (project.client && project.client.toLowerCase().includes(term)) ||
      (project.cin && project.cin.toLowerCase().includes(term)) ||
      (project.clients || []).some(c =>
        [c.nom, c.cin, c.ice, c.telephone].some(v => v && String(v).toLowerCase().includes(term))
      ) ||
      (project.representant?.nom && project.representant.nom.toLowerCase().includes(term)) ||
      (project.commune && project.commune.toLowerCase().includes(term)) ||
      (project.refGlobal && project.refGlobal.toLowerCase().includes(term)) ||
      (project.numeroDossier && project.numeroDossier.toLowerCase().includes(term)) ||
      (project.agentResponsable && project.agentResponsable.toLowerCase().includes(term))
    );
  });
};

// Tri projets
export const sortProjects = (projects, sortField, sortDirection) => {
  return [...projects].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Gérer les valeurs null/undefined
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    // Tri numérique pour certains champs
    if (['montantTotal', 'fraisSupplementaires'].includes(sortField)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }

    // Tri par reste à payer (calculé)
    if (sortField === 'resteAPayer') {
      aVal = calculateResteAPayer(a.montantTotal, a.avances, a.fraisSupplementaires);
      bVal = calculateResteAPayer(b.montantTotal, b.avances, b.fraisSupplementaires);
    }

    // Comparaison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};
